/**
 * Utilitários para conversão e formatação de fuso horário
 */

// Constante para o fuso horário local (GMT-3)
export const LOCAL_TIMEZONE = 'America/Sao_Paulo';

/**
 * Converte uma string de data/hora UTC para o fuso horário local
 * @param utcDateString - String de data no formato UTC
 * @returns Data convertida para o fuso horário local (GMT-3)
 */
export const convertToLocalTime = (utcDateString: string): Date => {
  const date = new Date(utcDateString);
  return date;
};

/**
 * Formata uma data para exibição com o fuso horário local
 * @param date - Data a ser formatada
 * @param options - Opções de formatação (padrão: data e hora completas)
 * @returns String formatada com data/hora no fuso horário local
 */
export const formatLocalTime = (
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: LOCAL_TIMEZONE
  }
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', options).format(dateObj);
};

/**
 * Retorna apenas a hora de uma data no formato HH:MM
 * @param date - Data para extrair a hora
 * @returns String com a hora no formato HH:MM
 */
export const formatHourMinute = (date: Date | string): string => {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: LOCAL_TIMEZONE
  };
  return formatLocalTime(date, options);
};

/**
 * Extrai apenas a hora (0-23) de uma data
 * @param date - Data para extrair a hora
 * @returns Número representando a hora (0-23)
 */
export const getHourFromDate = (date: Date | string): number => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.getHours();
};

/**
 * Extrai apenas os minutos (0-59) de uma data
 * @param date - Data para extrair os minutos
 * @returns Número representando os minutos (0-59)
 */
export const getMinuteFromDate = (date: Date | string): number => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.getMinutes();
};

/**
 * Converte a hora e minuto de uma data para minutos totais (ex: 1:30 = 90 minutos)
 * @param date - Data para converter
 * @returns Número total de minutos (hora * 60 + minutos)
 */
export const getTotalMinutesFromDate = (date: Date | string): number => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.getHours() * 60 + dateObj.getMinutes();
};

/**
 * Verifica se uma data é hoje no fuso horário local
 * @param date - Data a ser verificada
 * @returns true se a data for hoje, false caso contrário
 */
export const isToday = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  
  return (
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear()
  );
};

/**
 * Adiciona os métodos de formatação ao objeto global para uso em templates
 */
export const registerGlobalFormatters = (): void => {
  // @ts-ignore
  window.formatLocalTime = formatLocalTime;
  // @ts-ignore
  window.formatHourMinute = formatHourMinute;
}; 